import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Switch,
  Dialog,
  DialogContent,
  InputAdornment,
  MenuItem,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaVenusMars,
  FaShieldAlt,
  FaCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";

const EASE = [0.22, 1, 0.36, 1];

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.7rem",
      fontWeight: 700,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      mb: 0.6,
      display: "block",
    }}
  >
    {children}
  </Typography>
);

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    fontSize: "0.875rem",
    color: "#0f172a",
    background: "#f8fafc",
    transition: "all 0.2s",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#0288D1" },
    "&.Mui-focused fieldset": { borderColor: "#0288D1", borderWidth: 1.5 },
    "&.Mui-focused": { background: "#fff" },
  },
  "& input::placeholder, & textarea::placeholder": {
    color: "#cbd5e1",
    opacity: 1,
  },
};

const adornColor = (focused) => (focused ? "#0288D1" : "#cbd5e1");

export default function AddTeacherModal({ open, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [isClassAdmin, setIsClassAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setPhone("");
      setGender("");
      setIsClassAdmin(false);
      setFocusedField(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onCreate({ name, email, phone, gender, isClassAdmin });
    setSaving(false);
    toast.success("Teacher created successfully");
  };

  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 4 },
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
          width: {
            xs: "100%",
            sm: "100%",
          },
          maxWidth: {
            xs: "100%",
            sm: 500,
          },
          m: {
            xs: 0,
            sm: 2,
          },
          height: {
            xs: "100dvh",
            sm: "auto",
          },
          maxHeight: {
            xs: "100dvh",
            sm: "90vh",
          },
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(10px)",
          background: "rgba(10,20,40,0.55)",
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.38, ease: EASE }}
          >
            <DialogContent
              sx={{
                p: 0,
                overflow: "auto",
                maxHeight: {
                  xs: "100dvh",
                  sm: "90vh",
                },
              }}
            >
              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "20px",
                  border: "1px solid #e8edf5",
                  boxShadow:
                    "0 32px 80px rgba(2,60,120,0.14), 0 4px 16px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {/* ── Banner ── */}
                <Box
                  sx={{
                    height: 88,
                    background:
                      "linear-gradient(135deg, #0c1a2e 0%, #0d2d4f 50%, #0a3d6b 100%)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Decorative dots */}
                  {[
                    { top: 12, left: 24, size: 60, opacity: 0.06 },
                    { top: -10, right: 60, size: 90, opacity: 0.05 },
                    { bottom: -20, right: 20, size: 70, opacity: 0.07 },
                  ].map((d, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        top: d.top,
                        left: d.left,
                        right: d.right,
                        bottom: d.bottom,
                        width: d.size,
                        height: d.size,
                        borderRadius: "50%",
                        border: `1.5px solid rgba(255,255,255,${
                          d.opacity * 2
                        })`,
                        background: `rgba(255,255,255,${d.opacity})`,
                      }}
                    />
                  ))}

                  {/* Close btn */}
                  <IconButton
                    size="small"
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      color: "rgba(255,255,255,0.6)",
                      width: 28,
                      height: 28,
                      "&:hover": {
                        background: "rgba(255,255,255,0.12)",
                        color: "#fff",
                      },
                    }}
                  >
                    <FaTimes size={12} />
                  </IconButton>
                </Box>

                {/* ── Avatar floated over banner ── */}
                <Box
                  sx={{
                    px: 3,
                    pb: 0,
                    mt: "-30px",
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      fontSize: "1.1rem",
                      fontWeight: 800,
                      background: initials
                        ? "linear-gradient(135deg, #0288D1, #0ea5e9)"
                        : "linear-gradient(135deg, #cbd5e1, #e2e8f0)",
                      border: "3px solid #fff",
                      boxShadow: "0 4px 16px rgba(2,136,209,0.25)",
                      color: initials ? "#fff" : "#94a3b8",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {initials || <FaUser size={20} />}
                  </Avatar>

                  <Box sx={{ mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      New Teacher
                    </Typography>
                  </Box>
                </Box>

                {/* ── Name display under avatar ── */}
                <Box sx={{ px: 3, pt: 1, pb: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.05rem",
                      color: name ? "#0f172a" : "#cbd5e1",
                      letterSpacing: "-0.3px",
                      lineHeight: 1.2,
                      textTransform: "capitalize",
                      minHeight: 26,
                    }}
                  >
                    {name || "Teacher Name"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: email ? "#64748b" : "#e2e8f0",
                      mt: 0.2,
                      minHeight: 18,
                    }}
                  >
                    {email || "email@example.com"}
                  </Typography>
                </Box>

                <Divider sx={{ mx: 3, my: 2, borderColor: "#f1f5f9" }} />

                {/* ── Form ── */}
                <Box sx={{ px: 3, pb: 3 }}>
                  {/* Row: Full name */}
                  <Box sx={{ mb: 2 }}>
                    <FieldLabel>Full Name</FieldLabel>
                    <TextField
                      fullWidth
                      placeholder="e.g. Amara Johnson"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => setFocusedField(null)}
                      disabled={saving}
                      onKeyDown={(e) =>
                        e.key === "Enter" && name.trim() && handleSubmit()
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaUser
                              size={13}
                              color={adornColor(focusedField === "name")}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Box>

                  {/* Row: Email + Phone */}
                  <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel>Email</FieldLabel>
                      <TextField
                        fullWidth
                        placeholder="email@school.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocusedField("email")}
                        onBlur={() => setFocusedField(null)}
                        disabled={saving}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaEnvelope
                                size={12}
                                color={adornColor(focusedField === "email")}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FieldLabel>Phone</FieldLabel>
                      <TextField
                        fullWidth
                        placeholder="+234 800 000 0000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        onFocus={() => setFocusedField("phone")}
                        onBlur={() => setFocusedField(null)}
                        disabled={saving}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaPhone
                                size={12}
                                color={adornColor(focusedField === "phone")}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                      />
                    </Box>
                  </Stack>

                  {/* Row: Gender */}
                  <Box sx={{ mb: 2 }}>
                    <FieldLabel>Gender</FieldLabel>
                    <TextField
                      select
                      fullWidth
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      disabled={saving}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaVenusMars size={13} color="#cbd5e1" />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    >
                      <MenuItem value="" disabled>
                        <Typography
                          sx={{ color: "#cbd5e1", fontSize: "0.875rem" }}
                        >
                          Select gender
                        </Typography>
                      </MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </TextField>
                  </Box>

                  {/* Class Admin toggle */}
                  <Box
                    onClick={() => !saving && setIsClassAdmin((p) => !p)}
                    sx={{
                      mb: 3,
                      px: 2,
                      py: 1.2,
                      borderRadius: 2,
                      background: isClassAdmin
                        ? "rgba(2,136,209,0.05)"
                        : "#f8fafc",
                      border: `1.5px solid ${
                        isClassAdmin ? "rgba(2,136,209,0.22)" : "#e8edf5"
                      }`,
                      cursor: saving ? "default" : "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      sx={{ mb: 2 }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          background: isClassAdmin
                            ? "linear-gradient(135deg, #0288D1, #0ea5e9)"
                            : "#e8edf5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.25s",
                          boxShadow: isClassAdmin
                            ? "0 3px 8px rgba(2,136,209,0.28)"
                            : "none",
                        }}
                      >
                        <FaShieldAlt
                          size={13}
                          color={isClassAdmin ? "#fff" : "#94a3b8"}
                        />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: "0.84rem",
                            lineHeight: 1.2,
                          }}
                        >
                          Class Admin
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.7rem", color: "#94a3b8" }}
                        >
                          Can manage classes and students
                        </Typography>
                      </Box>
                    </Stack>
                    <Switch
                      checked={isClassAdmin}
                      disabled={saving}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => setIsClassAdmin((p) => !p)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#0288D1",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            background: "#0288D1",
                            opacity: 1,
                          },
                        "& .MuiSwitch-track": {
                          background: "#cbd5e1",
                          opacity: 1,
                        },
                      }}
                    />
                  </Box>

                  {/* Actions */}
                  <Stack direction="row" spacing={1.2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={onClose}
                      disabled={saving}
                      sx={{
                        py: 1.2,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: "0.84rem",
                        textTransform: "none",
                        borderColor: "#e2e8f0",
                        color: "#64748b",
                        "&:hover": {
                          borderColor: "#94a3b8",
                          background: "#f8fafc",
                        },
                      }}
                    >
                      Discard
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={saving || !name.trim()}
                      startIcon={saving ? null : <FaCheck size={11} />}
                      sx={{
                        py: 1.2,
                        borderRadius: 2,
                        fontWeight: 800,
                        fontSize: "0.84rem",
                        textTransform: "none",
                        background: "linear-gradient(135deg, #0c1a2e, #0d3460)",
                        boxShadow: "0 4px 14px rgba(12,26,46,0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #0d2040, #0f3d72)",
                          boxShadow: "0 6px 18px rgba(12,26,46,0.38)",
                        },
                        "&:disabled": {
                          background: "#e2e8f0",
                          color: "#94a3b8",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {saving ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={13} sx={{ color: "#fff" }} />
                          <span>Creating…</span>
                        </Stack>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </DialogContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
